import { useState } from "react";
import {
  Clock, Trash2, RotateCcw, UserCog, ShieldAlert, X,
} from "lucide-react";

/**
 * Users management tab for /admin:
 *  - sub-tabs (active / deleted)
 *  - active users table with last-login, stale + demo badges, role pill,
 *    soft-delete inline action, Verwalten button
 *  - deleted users table with restore + hard-delete actions
 *  - stale-only filter pill
 *
 * Stateless re-render of data lifted to <Admin>; only sub-tab + filter are
 * local UI state.
 */
export const UsersTab = ({
  users,
  deletedUsers,
  currentUser,
  onManage,
  onSoftDelete,
  onRestore,
  onHardDelete,
}) => {
  const [userSubTab, setUserSubTab] = useState("active");
  const [showStaleOnly, setShowStaleOnly] = useState(false);

  const staleCount = users.filter(x => x.is_stale).length;
  const visibleUsers = showStaleOnly ? users.filter(x => x.is_stale) : users;

  return (
    <div className="mt-6 space-y-4">
      {/* Sub-tabs: Active / Deleted + stale filter */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setUserSubTab("active")}
          className={`pill-tab ${userSubTab === "active" ? "active" : ""}`}
          data-testid="users-subtab-active"
        >
          Aktive Nutzer <span className="ml-1 opacity-60">({users.length})</span>
        </button>
        <button
          onClick={() => setUserSubTab("deleted")}
          className={`pill-tab ${userSubTab === "deleted" ? "active" : ""}`}
          data-testid="users-subtab-deleted"
        >
          Gelöschte Nutzer <span className="ml-1 opacity-60">({deletedUsers.length})</span>
        </button>
        {userSubTab === "active" && (
          <button
            onClick={() => setShowStaleOnly(s => !s)}
            className={`pill-tab ml-auto ${showStaleOnly ? "active" : ""}`}
            data-testid="users-filter-stale"
            title="Nur Accounts, die seit über 90 Tagen nicht aktiv waren"
          >
            <Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
            Veraltete Accounts <span className="ml-1 opacity-60">({staleCount})</span>
          </button>
        )}
      </div>

      {userSubTab === "active" && (
        <div className="nv-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-novatus-card-2 border-b border-novatus">
              <tr className="text-left">
                <th className="px-4 py-3 label-eyebrow">Name</th>
                <th className="px-4 py-3 label-eyebrow">E-Mail</th>
                <th className="px-4 py-3 label-eyebrow">Rolle</th>
                <th className="px-4 py-3 label-eyebrow">Letzter Login</th>
                <th className="px-4 py-3 label-eyebrow">Level</th>
                <th className="px-4 py-3 label-eyebrow text-right">XP</th>
                <th className="px-4 py-3 label-eyebrow text-right">Trainings</th>
                <th className="px-4 py-3 label-eyebrow text-right">Ø Score</th>
                <th className="px-4 py-3 label-eyebrow text-right">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map(u => {
                const isSelf = currentUser?.id === u.id;
                const isProtected = !!u.is_protected;
                const directorBlocked = currentUser?.role === "direktor" && ["direktor", "admin"].includes(u.role);
                const canDelete = !isSelf && !isProtected && !directorBlocked;
                return (
                  <tr key={u.id} className={`border-b border-novatus hover:bg-white/[0.02] ${u.is_stale ? "bg-amber-500/[0.03]" : ""}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{u.first_name} {u.last_name}</span>
                        {u.is_stale && (
                          <span
                            className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 inline-flex items-center gap-1"
                            title="Seit über 90 Tagen nicht aktiv — kann manuell gelöscht werden"
                            data-testid={`stale-badge-${u.id}`}
                          >
                            <Clock className="w-2.5 h-2.5" /> Veraltet
                          </span>
                        )}
                        {u.is_demo_account && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-blue-400/15 border border-blue-400/40 text-blue-300" title="Demo-Account">
                            Demo
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-novatus-card-2 border border-novatus text-novatus-text-2">{u.role}</span>
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2 text-xs whitespace-nowrap" data-testid={`last-login-${u.id}`}>
                      {u.last_login_at
                        ? new Date(u.last_login_at).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
                        : <span className="text-novatus-text-3 italic">noch nie</span>}
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2">{u.level}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{u.xp || 0}</td>
                    <td className="px-4 py-3 text-right font-mono text-white">{u.total_sessions}</td>
                    <td className="px-4 py-3 text-right font-display font-black text-novatus-green">{u.average_score}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => onManage(u)}
                          className="px-2.5 py-1 text-xs rounded-md border border-novatus bg-novatus-card-2 hover:bg-white/5 text-white inline-flex items-center gap-1.5"
                          data-testid={`user-manage-${u.id}`}
                          title="Details & Aktionen"
                        >
                          <UserCog className="w-3.5 h-3.5" /> Verwalten
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => onSoftDelete(u)}
                            className="p-1.5 rounded-md hover:bg-amber-500/10 text-amber-400"
                            title="Anonymisieren (Soft-Delete)"
                            data-testid={`user-soft-delete-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                        {isProtected && (
                          <span className="text-[10px] text-novatus-text-3 inline-flex items-center gap-1" title="Systemgeschützt">
                            <ShieldAlert className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {userSubTab === "deleted" && (
        <div className="nv-card overflow-x-auto">
          {deletedUsers.length === 0 ? (
            <div className="px-6 py-10 text-center text-novatus-text-3 text-sm">
              Keine gelöschten Nutzer.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-novatus-card-2 border-b border-novatus">
                <tr className="text-left">
                  <th className="px-4 py-3 label-eyebrow">Ursprünglicher Name</th>
                  <th className="px-4 py-3 label-eyebrow">Ursprüngliche E-Mail</th>
                  <th className="px-4 py-3 label-eyebrow">Gelöscht am</th>
                  <th className="px-4 py-3 label-eyebrow">Gelöscht von</th>
                  <th className="px-4 py-3 label-eyebrow text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {deletedUsers.map(u => (
                  <tr key={u.id} className="border-b border-novatus hover:bg-white/[0.02]" data-testid={`deleted-user-row-${u.id}`}>
                    <td className="px-4 py-3 font-semibold text-white">
                      {u.original_first_name || "—"} {u.original_last_name || ""}
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2 font-mono text-xs">
                      {u.original_email || "—"}
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2 text-xs">
                      {u.deleted_at ? new Date(u.deleted_at).toLocaleString("de-DE") : "—"}
                    </td>
                    <td className="px-4 py-3 text-novatus-text-2 text-xs">{u.deleted_by || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => onRestore(u)}
                          className="px-2.5 py-1 text-xs rounded-md border border-novatus-green/40 bg-novatus-green/10 hover:bg-novatus-green/20 text-novatus-green inline-flex items-center gap-1.5"
                          data-testid={`user-restore-${u.id}`}
                          title="Wiederherstellen"
                        >
                          <RotateCcw className="w-3.5 h-3.5" /> Wiederherstellen
                        </button>
                        <button
                          onClick={() => onHardDelete(u)}
                          className="px-2.5 py-1 text-xs rounded-md border border-red-500/40 bg-red-500/10 hover:bg-red-500/20 text-red-400 inline-flex items-center gap-1.5"
                          data-testid={`user-hard-delete-${u.id}`}
                          title="Endgültig löschen"
                        >
                          <X className="w-3.5 h-3.5" /> Endgültig löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};
